import { formatAmount } from '@tms/core';
import type { FormatAmountOptions } from '@tms/core';
import type { HTMLAttributes } from 'react';

import styles from './Amount.module.css';

export type AmountTone = 'credit' | 'debit' | 'neutral' | 'muted';

export interface AmountProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** The amount, in paise. Never a rupee float - see @tms/core. */
  readonly amountMinor: number;
  readonly tone?: AmountTone;
  readonly size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Passed through to the formatter: paise on or off, symbol on or off, signed. */
  readonly format?: FormatAmountOptions;
}

/**
 * Every amount on every screen (`LED-01`, `LED-05`, `SUB-02`, and everything after them).
 *
 * Two things it will not let a caller get wrong. It takes paise, so there is no path by which a rupee
 * float reaches a screen. And it carries the tabular-figures rule, so an amount cannot be rendered in the
 * body font by forgetting a class.
 */
export function Amount({
  amountMinor,
  tone = 'neutral',
  size = 'md',
  format,
  className,
  ...rest
}: AmountProps): React.JSX.Element {
  const text = formatAmount(amountMinor, format ?? {});

  return (
    <span
      className={[styles.amount, styles[tone], styles[size], className ?? ''].filter(Boolean).join(' ')}
      {...rest}
    >
      {text}
    </span>
  );
}
